import { useEffect, useState } from "react";
import { api, formatApiError, getUpgradeInfo } from "../../lib/api";
import { UpgradeNotice, LockedFeature } from "../plan/PlanUI";
import { X, Loader2, Network, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const BRANCH_COLORS = ["#8b5cf6", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#f97316"];

// Nó expansível do mapa (clica pra abrir/fechar os filhos)
function MapNode({ node, color, depth }) {
  const [open, setOpen] = useState(depth === 0);
  const kids = node.filhos || [];
  const hasKids = kids.length > 0;
  return (
    <div className={depth > 0 ? "ml-4 border-l-2 pl-3 py-0.5" : ""} style={depth > 0 ? { borderColor: `${color}55` } : undefined}>
      <button
        type="button"
        onClick={() => hasKids && setOpen((o) => !o)}
        className={`flex items-start gap-1.5 text-left w-full rounded-lg px-2 py-1.5 transition ${hasKids ? "hover:bg-muted cursor-pointer" : "cursor-default"}`}
      >
        {hasKids ? (
          open ? <ChevronDown className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color }} /> : <ChevronRight className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color }} />
        ) : (
          <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
        )}
        <span className="min-w-0">
          <span className={`block ${depth === 0 ? "font-bold" : "font-medium"} text-sm`}>{node.nome}</span>
          {node.detalhe && <span className="block text-xs text-muted-foreground mt-0.5">{node.detalhe}</span>}
        </span>
      </button>
      {open && hasKids && (
        <div className="space-y-0.5">
          {kids.map((k, i) => (
            <MapNode key={`${k.nome}-${i}`} node={k} color={color} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function MindMapModal({ open, onClose, conversationId, locked }) {
  const [doc, setDoc] = useState(null);
  const [topico, setTopico] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [upgrade, setUpgrade] = useState(null);

  // Carrega o último mapa desta conversa ao abrir
  useEffect(() => {
    if (!open || locked || !conversationId) return;
    api.get(`/mindmap?conversation_id=${conversationId}`)
      .then((r) => setDoc(r.data || null))
      .catch(() => {});
  }, [open, locked, conversationId]);

  if (!open) return null;

  const generate = async () => {
    setBusy(true); setErr(null); setUpgrade(null);
    try {
      const { data } = await api.post("/mindmap/generate", {
        conversation_id: conversationId,
        topico: topico.trim() || null,
      });
      setDoc(data);
    } catch (e) {
      const up = getUpgradeInfo(e);
      if (up) setUpgrade(up);
      else setErr(formatApiError(e));
    }
    setBusy(false);
  };

  const mapa = doc?.mapa;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid="mindmap-modal" className="w-full sm:max-w-2xl bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-violet-400" />
            <h2 className="font-bold tracking-tight">Mapa mental</h2>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin">
          {locked ? (
            <LockedFeature
              titulo="Mapa mental interativo"
              descricao="A matéria inteira organizada num mapa que você expande tópico por tópico — gerado com o melhor modelo do plano Pro."
              planoNecessario="pro"
            />
          ) : (
            <>
              {upgrade && <UpgradeNotice info={upgrade} onDismiss={() => setUpgrade(null)} />}
              {!mapa && (
                <p className="text-sm text-muted-foreground">
                  Vai organizar o material e o contexto desta conversa num mapa hierárquico. Se quiser, foca num tópico:
                </p>
              )}
              <div className="flex gap-2">
                <input
                  value={topico}
                  onChange={(e) => setTopico(e.target.value)}
                  placeholder="Tópico (opcional) — ex.: Funções orgânicas"
                  className="flex-1 rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50"
                />
                <button
                  data-testid="mindmap-generate"
                  onClick={generate}
                  disabled={busy}
                  className="shrink-0 rounded-full bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mapa ? <RefreshCw className="h-3.5 w-3.5" /> : null}
                  {mapa ? "Refazer" : "Gerar mapa"}
                </button>
              </div>
              {err && <p className="text-sm text-red-400">{err}</p>}
              {mapa && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-violet-500/40 bg-accent px-4 py-3 text-center">
                    <p className="font-bold tracking-tight">{mapa.titulo}</p>
                  </div>
                  <div className="space-y-3">
                    {(mapa.ramos || []).map((ramo, i) => {
                      const color = BRANCH_COLORS[i % BRANCH_COLORS.length];
                      return (
                        <div key={`${ramo.nome}-${i}`} className="rounded-xl border border-border bg-secondary p-3">
                          <MapNode node={ramo} color={color} depth={0} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
